import { Fragment, useMemo } from 'react';
import { UnstyledButton } from '@mantine/core';
import { IconAlarm, IconPlayerPause, IconPlayerPlay } from '@tabler/icons-react';
import { displayFractions } from '../../utils/formatQuantity';
import { formatDurationLabel, parseStepDurations } from '../../utils/stepDurations';
import { TimerOrigin, timerRemainingMs, useCookingTimers } from '../../contexts/CookingTimersContext';

type StepTimerTextProps = {
  /** Raw step text; fractions are prettified here. */
  text: string;
  /** Stable per-step identity, e.g. `${recipe.id}:${stepIndex}`. */
  timerKey: string;
  stepNumber: number;
  recipeName?: string;
  /** Where to return when the timer is tapped in the tray. */
  origin?: TimerOrigin;
};

/**
 * Step text with each duration ("bake 30 minutes") rendered as an inline
 * tap-to-start timer chip. Tapping cycles start → pause → resume; a finished
 * timer's chip restarts it.
 */
export const StepTimerText = ({ text, timerKey, stepNumber, recipeName, origin }: StepTimerTextProps) => {
  const ctx = useCookingTimers();
  const display = useMemo(() => displayFractions(text), [text]);
  const durations = useMemo(() => parseStepDurations(display), [display]);

  if (!ctx || durations.length === 0) return <>{display}</>;

  const { timers, now, startTimer, pauseTimer, resumeTimer } = ctx;
  const parts: React.ReactNode[] = [];
  let cursor = 0;

  durations.forEach((d, i) => {
    if (d.start > cursor) parts.push(<Fragment key={`t${i}`}>{display.slice(cursor, d.start)}</Fragment>);
    cursor = d.end;

    const key = `${timerKey}:${i}`;
    const timer = timers.find((t) => t.key === key);
    const status = timer?.status;
    const label = `Step ${stepNumber} · ${formatDurationLabel(d.seconds)}`;

    const onClick = (e: React.MouseEvent) => {
      // Step cards have their own click handlers (ingredient highlighting).
      e.stopPropagation();
      if (!timer || status === 'done') startTimer({ key, label, recipeName, seconds: d.seconds, origin });
      else if (status === 'running') pauseTimer(timer.id);
      else resumeTimer(timer.id);
    };

    const color = status === 'done' ? 'orange' : status ? 'teal' : 'blue';
    const Icon = status === 'running' ? IconPlayerPause : status === 'paused' ? IconPlayerPlay : IconAlarm;
    const ariaLabel =
      status === 'running' ? `Pause ${d.text} timer`
        : status === 'paused' ? `Resume ${d.text} timer`
        : `Start ${formatDurationLabel(d.seconds)} timer`;

    // The chip's contents never change size, so starting a timer can't
    // rewrap the step text. Progress shows as a background fill instead of
    // an inline countdown; the tray has the exact time.
    const elapsedPct = timer && status !== 'done'
      ? Math.min(100, Math.max(0, 100 - (timerRemainingMs(timer, now) / (timer.durationSeconds * 1000)) * 100))
      : 0;
    const light = `var(--mantine-color-${color}-light)`;
    const fill = `var(--mantine-color-${color}-2)`;

    parts.push(
      <UnstyledButton
        key={`c${i}`}
        onClick={onClick}
        aria-label={ariaLabel}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '0 6px',
          margin: '0 1px',
          borderRadius: 6,
          font: 'inherit',
          lineHeight: 'inherit',
          verticalAlign: 'baseline',
          color: `var(--mantine-color-${color}-8)`,
          background: timer && status !== 'done'
            ? `linear-gradient(to right, ${fill} ${elapsedPct}%, ${light} ${elapsedPct}%)`
            : light,
          whiteSpace: 'nowrap',
        }}
      >
        <Icon size="0.95em" stroke={2} style={{ flexShrink: 0 }} />
        <span style={{ fontWeight: 600 }}>{d.text}</span>
      </UnstyledButton>,
    );
  });
  if (cursor < display.length) parts.push(<Fragment key="tail">{display.slice(cursor)}</Fragment>);

  return <>{parts}</>;
};
